import { useCallback, useEffect, useRef, useState } from 'react';

import { opsVisibilityApi } from 'api/opsVisibility';

/**
 * Custom hook for fetching OOB and Frozen manifest data for a route.
 *
 * Fetches both manifests in parallel using the existing opsVisibility API.
 * Aborts in-flight requests when inputs change or the component unmounts.
 *
 * @param {Object} params
 * @param {number|null} params.lineHaulId - Line haul ID for the route
 * @param {string|null} params.packDate - Pack date in YYYY-MM-DD format
 * @returns {{ oobManifest: Object|null, frozenManifest: Object|null, loading: boolean, error: string|null }}
 */
const useManifestData = ({ lineHaulId, packDate }) => {
    const hasInputs = Boolean(lineHaulId && packDate);
    const [oobManifest, setOobManifest] = useState(null);
    const [frozenManifest, setFrozenManifest] = useState(null);
    const [loading, setLoading] = useState(hasInputs);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);

    const fetchManifests = useCallback(async () => {
        if (!lineHaulId || !packDate) {
            setOobManifest(null);
            setFrozenManifest(null);
            setError(null);
            setLoading(false);
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        setError(null);

        try {
            const [oobResponse, frozenResponse] = await Promise.all([
                opsVisibilityApi.getOutOfBoxManifest(lineHaulId, packDate),
                opsVisibilityApi.getFrozenManifest(lineHaulId, packDate),
            ]);

            if (abortController.signal.aborted) {
                return;
            }

            const oobFailed = !oobResponse.status;
            const frozenFailed = !frozenResponse.status;

            if (oobFailed && frozenFailed) {
                setError('Failed to fetch manifest data');
                setOobManifest(null);
                setFrozenManifest(null);
            } else {
                setOobManifest(oobFailed ? null : oobResponse.data || null);
                setFrozenManifest(frozenFailed ? null : frozenResponse.data || null);
            }
        } catch (err) {
            if (abortController.signal.aborted) {
                return;
            }
            setOobManifest(null);
            setFrozenManifest(null);
            setError(err.message || 'An unexpected error occurred');
        } finally {
            if (!abortController.signal.aborted) {
                setLoading(false);
            }
        }
    }, [lineHaulId, packDate]);

    useEffect(() => {
        fetchManifests();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchManifests]);

    return { oobManifest, frozenManifest, loading, error };
};

export { useManifestData };
